import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import Iframe from "react-iframe";
import { Redirect, useParams } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheck,
  FaClipboardList,
  FaFileAlt,
  FaSyncAlt,
  FaUserGraduate,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import SelectClass from "../ChildComponent/SelectClass";
import MdlSelectSingleStudent from "../dialog/MdlSelectSingleStudent";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/StudentAttendanceReport.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const DEFAULT_IFRAME_HEIGHT = "300px";

const REPORT_MODE_CLASS = "class";
const REPORT_MODE_SINGLE = "single";

// Created By Cursor
function getCurrentYearMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

// Created By Cursor
function parseLooseDate(raw) {
  if (!raw) return null;
  const value = String(raw).trim();
  let m = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = value.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
  const fallback = new Date(value);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

// Created By Cursor — months from session start → end
function buildSessionMonths(startRaw, endRaw) {
  const session = SessionManager.getCurrentSession()?.[0];
  const start =
    parseLooseDate(startRaw) || parseLooseDate(session?.start_date);
  const end = parseLooseDate(endRaw) || parseLooseDate(session?.end_date);
  if (!start || !end) return [getCurrentYearMonth()];
  const months = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const last = new Date(end.getFullYear(), end.getMonth(), 1);
  if (cursor > last) return [getCurrentYearMonth()];
  while (cursor <= last) {
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, "0");
    months.push(`${year}-${month}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months.length ? months : [getCurrentYearMonth()];
}

// Created By Cursor
function pickDefaultMonth(months) {
  const current = getCurrentYearMonth();
  if (!months?.length) return current;
  if (months.includes(current)) return current;
  if (current < months[0]) return months[0];
  return months[months.length - 1];
}

// Created By Cursor
function formatMonthLabel(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// Created By Cursor
function formatMonthShort(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return ym || "—";
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "short" });
}

// Created By Cursor
function monthYear(ym) {
  const m = String(ym || "").match(/^(\d{4})-(\d{2})$/);
  return m ? m[1] : "";
}

// Created By Cursor
function buildPreviewUrl({
  fileDirectory,
  fileName,
  classId,
  sectionId,
  classSectionName,
  sessionId,
  month,
  sampleId,
  studentEnrollmentNo,
  sessionStart,
  sessionEnd,
}) {
  if (!fileDirectory || !fileName || !classId || !sessionId) {
    return "";
  }
  if (!studentEnrollmentNo && !month) {
    return "";
  }
  const SCHOOL_CODE = SessionManager.getSchoolCode();
  const dir = String(fileDirectory).endsWith("/")
    ? fileDirectory
    : `${fileDirectory}/`;
  const params = new URLSearchParams({
    class_id: classId,
    section_id: sectionId || "No",
    class_section_name: classSectionName || "",
    session_id: sessionId,
    sample_id: sampleId || "",
    system_code: process.env.REACT_APP_SYSTEM_CODE || "",
    school_code: SCHOOL_CODE || "",
  });
  if (studentEnrollmentNo) {
    params.set("student_enrollment_no", studentEnrollmentNo);
    if (sessionStart) params.set("session_start", sessionStart);
    if (sessionEnd) params.set("session_end", sessionEnd);
  } else {
    params.set("month", month);
  }
  return `${process.env.REACT_APP_SERVER_NAME}${dir}${fileName}?${params.toString()}`;
}

export default function StudentAttendanceReport() {
  const loggedIn = localStorage.getItem("token") != null;
  const { document_type: documentType } = useParams();

  const [loadingSamples, setLoadingSamples] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);

  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStart, setSessionStart] = useState("");
  const [sessionEnd, setSessionEnd] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(-1);
  const [classSectionName, setClassSectionName] = useState("");
  const [reportMode, setReportMode] = useState(REPORT_MODE_CLASS);
  const [pickedStudent, setPickedStudent] = useState(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState("");
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);

  const [printableSamples, setPrintableSamples] = useState([]);
  const [selectedSampleId, setSelectedSampleId] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileDirectory, setFileDirectory] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() =>
    pickDefaultMonth(buildSessionMonths())
  );
  const [refreshNonce, setRefreshNonce] = useState(0);

  const monthOptions = useMemo(
    () => buildSessionMonths(sessionStart, sessionEnd),
    [sessionStart, sessionEnd]
  );
  const currentYearMonth = useMemo(() => getCurrentYearMonth(), []);

  const selectedSample = useMemo(() => {
    if (!printableSamples.length) return null;
    return (
      printableSamples.find(
        (s) => String(s.sample_id) === String(selectedSampleId)
      ) || printableSamples[0]
    );
  }, [printableSamples, selectedSampleId]);

  const selectedStudent = pickedStudent;

  const classSelected = selectedClassPosition >= 0 && Boolean(classId);
  const canPreview =
    reportMode === REPORT_MODE_CLASS
      ? classSelected
      : Boolean(selectedEnrollment && classId && sessionId);

  const fileUrl = useMemo(() => {
    if (!canPreview) {
      return "";
    }
    if (reportMode === REPORT_MODE_SINGLE && !selectedEnrollment) {
      return "";
    }
    return buildPreviewUrl({
      fileDirectory,
      fileName,
      classId,
      sectionId,
      classSectionName,
      sessionId,
      month: selectedMonth,
      sampleId: selectedSampleId,
      studentEnrollmentNo:
        reportMode === REPORT_MODE_SINGLE ? selectedEnrollment : "",
      sessionStart,
      sessionEnd,
    });
  }, [
    canPreview,
    fileDirectory,
    fileName,
    classId,
    sectionId,
    classSectionName,
    sessionId,
    selectedMonth,
    selectedSampleId,
    reportMode,
    selectedEnrollment,
    sessionStart,
    sessionEnd,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!monthOptions.length) return;
    setSelectedMonth(pickDefaultMonth(monthOptions));
  }, [sessionStart, sessionEnd, monthOptions]);

  // Created By Cursor — same as StudentDueList / TransportReport
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.iframeHeight) {
        setIframeHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!canPreview || !fileUrl) {
      setIframeHeight(DEFAULT_IFRAME_HEIGHT);
      setIframeLoading(false);
      return undefined;
    }
    console.log("[StudentAttendanceReport] URL>>>", fileUrl);
    setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    setIframeLoading(true);
    const safety = setTimeout(() => setIframeLoading(false), 25000);
    return () => clearTimeout(safety);
  }, [fileUrl, refreshNonce, canPreview]);

  const applySample = useCallback((sample) => {
    if (!sample) return;
    setSelectedSampleId(String(sample.sample_id || ""));
    setFileName(sample.file_name || "");
    setFileDirectory(sample.file_directory || "");
  }, []);

  const getPrintableSamples = useCallback(async () => {
    setLoadingSamples(true);
    try {
      const response = await axios
        .get(`${API_BASE()}getPrintableSample.php`, {
          params: {
            school_code: SessionManager.getSchoolCode(),
            document_type: documentType || "",
          },
        })
        .then((res) => res.data);

      if (response.api_status === "OK" && Array.isArray(response.data)) {
        setPrintableSamples(response.data);
        if (response.data[0]) applySample(response.data[0]);
      } else {
        setPrintableSamples([]);
        myToast.notify_warning(
          response.api_remark || "No printable samples found."
        );
      }
    } catch (error) {
      console.error("[StudentAttendanceReport] samples error", error);
      myToast.notify_error("Failed to load report templates.");
    } finally {
      setLoadingSamples(false);
    }
  }, [applySample, documentType]);

  const applySessionDates = useCallback((pickedSessionId, fallbackName) => {
    const session = SessionManager.getCurrentSession()?.[0];
    if (session && String(session.session_id) === String(pickedSessionId)) {
      setSessionStart(session.start_date || "");
      setSessionEnd(session.end_date || "");
      setSessionName(session.session_name || fallbackName || "");
      return;
    }
    setSessionStart("");
    setSessionEnd("");
    if (fallbackName) setSessionName(fallbackName);
  }, []);

  const callBackClassSelect = useCallback(
    (
      class_id,
      section_id,
      session_id,
      selected_position,
      class_name,
      section_name,
      session_name,
      start_date,
      end_date
    ) => {
      setClassId(class_id);
      setSectionId(section_id);
      setSessionId(session_id);
      setSessionName(session_name || "");
      setSessionStart(start_date || "");
      setSessionEnd(end_date || "");
      setSelectedClassPosition(selected_position);
      setClassSectionName(
        section_id === "No" || !section_name
          ? class_name || ""
          : `${class_name}:${section_name}`
      );
      setSelectedEnrollment("");
      setPickedStudent(null);

      if (!printableSamples.length) {
        getPrintableSamples();
      }
    },
    [getPrintableSamples, printableSamples.length]
  );

  const handleSelectedStudent = useCallback(
    (enrollmentNo, student, pickedSessionId, pickedSessionName) => {
      const enrollment =
        enrollmentNo || student?.student_enrollment_no || "";
      if (!enrollment || !student) {
        myToast.notify_warning("Please select a student.");
        return;
      }

      const nextClassId = student.class_id || "";
      const nextSectionId = student.section_id || "No";
      const className = student.class_name || "";
      const sectionName = student.section_name || "";
      const nextSessionId = pickedSessionId || student.session_id || "";

      setPickedStudent(student);
      setSelectedEnrollment(enrollment);
      setClassId(nextClassId);
      setSectionId(nextSectionId);
      setSessionId(nextSessionId);
      setSelectedClassPosition(-1);
      setClassSectionName(
        nextSectionId === "No" || !sectionName
          ? className
          : `${className}:${sectionName}`
      );
      applySessionDates(nextSessionId, pickedSessionName || "");
      setStudentPickerOpen(false);

      if (!printableSamples.length) {
        getPrintableSamples();
      }
    },
    [applySessionDates, getPrintableSamples, printableSamples.length]
  );

  const handleReportModeChange = useCallback((mode) => {
    setIframeLoading(false);
    setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    if (mode === REPORT_MODE_SINGLE) {
      setReportMode(REPORT_MODE_SINGLE);
      if (!pickedStudent) {
        setStudentPickerOpen(true);
      }
      return;
    }
    setReportMode(REPORT_MODE_CLASS);
    setStudentPickerOpen(false);
    setPickedStudent(null);
    setSelectedEnrollment("");
  }, [pickedStudent]);

  const handleMonthChange = useCallback((value) => {
    setSelectedMonth(value);
  }, []);

  const handleSampleSelect = useCallback(
    (sample) => {
      if (!sample) return;
      applySample(sample);
    },
    [applySample]
  );

  const handleRefresh = useCallback(() => {
    if (!canPreview) return;
    setRefreshing(true);
    if (!printableSamples.length) {
      getPrintableSamples().finally(() => setRefreshing(false));
      return;
    }
    setIframeLoading(true);
    setIframeHeight(DEFAULT_IFRAME_HEIGHT);
    setRefreshNonce((n) => n + 1);
    setTimeout(() => setRefreshing(false), 400);
  }, [canPreview, getPrintableSamples, printableSamples.length]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page trep-page sar-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}>
                {" "}
                Student Attendance Report
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardList />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Student Attendance Report</h1>
              <p className="sm-hero__sub">
                {canPreview
                  ? `${classSectionName || "Selected class"}${
                      selectedStudent?.stu_name
                        ? ` · ${selectedStudent.stu_name}`
                        : ""
                    }${
                      reportMode === REPORT_MODE_CLASS
                        ? ` · ${formatMonthLabel(selectedMonth)}`
                        : " · all months"
                    }${sessionName ? ` · ${sessionName}` : ""}`
                  : reportMode === REPORT_MODE_SINGLE
                  ? "Select a student to preview the full-session report"
                  : "Choose class and month to preview the report"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing || loadingSamples || !canPreview}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh preview"
              aria-label="Refresh preview"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="atd-nav-actions sar-nav-actions" aria-label="Report type">
          <button
            type="button"
            className={`atd-nav-btn${
              reportMode === REPORT_MODE_CLASS ? " is-active" : ""
            }`}
            onClick={() => handleReportModeChange(REPORT_MODE_CLASS)}
            aria-pressed={reportMode === REPORT_MODE_CLASS}
          >
            <span className="atd-nav-btn__icon" aria-hidden>
              <FaUsers />
            </span>
            <span className="atd-nav-btn__text">
              <strong>Class Student attendance</strong>
              <small>Full class · all students for the month</small>
            </span>
          </button>
          <button
            type="button"
            className={`atd-nav-btn${
              reportMode === REPORT_MODE_SINGLE ? " is-active" : ""
            }`}
            onClick={() => handleReportModeChange(REPORT_MODE_SINGLE)}
            aria-pressed={reportMode === REPORT_MODE_SINGLE}
          >
            <span className="atd-nav-btn__icon is-list" aria-hidden>
              <FaUserGraduate />
            </span>
            <span className="atd-nav-btn__text">
              <strong>Single Student Attendance</strong>
              <small>One student · all months of the session</small>
            </span>
          </button>
        </div>

        <div className="tr-content trep-content sar-content">
          {reportMode === REPORT_MODE_CLASS ? (
            <div className="sar-class-wrap">
              <SelectClass
                action={callBackClassSelect}
                selected_position={selectedClassPosition}
              />
            </div>
          ) : (
            <section className="trep-panel" aria-label="Selected student">
              <div className="trep-panel__head">
                <h2 className="trep-panel__title">
                  <FaUserGraduate aria-hidden /> Student
                </h2>
                <span className="trep-panel__count">
                  {selectedStudent?.stu_name || "Not selected"}
                </span>
              </div>
              {selectedStudent ? (
                <div className="sar-student-card">
                  {selectedStudent.student_img_path ? (
                    <img
                      className="sar-student-card__photo"
                      src={selectedStudent.student_img_path}
                      alt={selectedStudent.stu_name || "Student"}
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="sar-student-card__photo is-placeholder" aria-hidden>
                      <FaUserGraduate />
                    </div>
                  )}
                  <div className="sar-student-card__info">
                    <p className="sar-student-card__name">
                      {selectedStudent.stu_name || "Student"}
                    </p>
                    <p className="sar-student-card__meta">
                      Enrollment: {selectedStudent.student_enrollment_no || "—"}
                      {classSectionName ? ` · Class: ${classSectionName}` : ""}
                      {selectedStudent.student_roll_no
                        ? ` · Roll: ${selectedStudent.student_roll_no}`
                        : ""}
                    </p>
                    {selectedStudent.father_name ? (
                      <p className="sar-student-card__meta">
                        S/O {selectedStudent.father_name}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="sar-student-card__change"
                    onClick={() => setStudentPickerOpen(true)}
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div className="sar-student-pick">
                  <button
                    type="button"
                    className="sar-student-select-btn"
                    onClick={() => setStudentPickerOpen(true)}
                  >
                    <FaUserGraduate aria-hidden />
                    Select student
                  </button>
                </div>
              )}
            </section>
          )}

          {reportMode === REPORT_MODE_CLASS ? (
          <section className="trep-panel sar-month-panel" aria-label="Select month">
            <div className="trep-panel__head sar-month-panel__head">
              <div className="min-w-0">
                <h2 className="trep-panel__title">
                  <FaCalendarAlt aria-hidden /> Attendance month
                </h2>
              </div>
              <span className="trep-panel__count sar-month-panel__badge">
                {formatMonthLabel(selectedMonth)}
              </span>
            </div>

            <div className="sar-month-body">
              <div className="sar-month-track">
                <div
                  className="sar-month-grid"
                  role="group"
                  aria-label="Session months"
                  style={{
                    "--sar-month-count": Math.max(monthOptions.length, 1),
                  }}
                >
                  {monthOptions.map((item) => {
                    const active = item === selectedMonth;
                    const isCurrent = item === currentYearMonth;
                    return (
                      <button
                        key={item}
                        type="button"
                        className={`sar-month-chip${active ? " is-active" : ""}${
                          isCurrent && !active ? " is-current" : ""
                        }`}
                        onClick={() => handleMonthChange(item)}
                        aria-pressed={active}
                        title={formatMonthLabel(item)}
                      >
                        <span className="sar-month-chip__name">
                          {formatMonthShort(item)}
                        </span>
                        <span className="sar-month-chip__year">
                          {isCurrent ? "Now" : monthYear(item)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
          ) : null}

          {printableSamples.length > 1 ? (
            <section className="trep-panel" aria-label="Report layout">
              <div className="trep-panel__head">
                <h2 className="trep-panel__title">
                  <FaFileAlt aria-hidden /> Report layout
                </h2>
              </div>
              <div className="trep-chips" role="group" aria-label="Samples">
                {printableSamples.map((sample, index) => {
                  const id = String(sample.sample_id || index);
                  const active =
                    String(selectedSample?.sample_id || "") ===
                    String(sample.sample_id || "");
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`trep-chip${active ? " is-active" : ""}`}
                      onClick={() => handleSampleSelect(sample)}
                    >
                      {active ? (
                        <FaCheck aria-hidden />
                      ) : (
                        <FaFileAlt aria-hidden />
                      )}
                      <span>
                        {sample.sample_name ||
                          sample.file_name ||
                          `Sample ${index + 1}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          <section className="trep-preview" aria-label="Report preview">
            <div className="trep-preview__head">
              <h2 className="trep-preview__title">Preview</h2>
              <p className="trep-preview__sub">
                {canPreview
                  ? `${classSectionName || "Class"}${
                      selectedStudent?.stu_name
                        ? ` · ${selectedStudent.stu_name}`
                        : ""
                    }${
                      reportMode === REPORT_MODE_CLASS
                        ? ` · ${formatMonthLabel(selectedMonth)}`
                        : " · all months"
                    }`
                  : reportMode === REPORT_MODE_SINGLE
                  ? "Select a student to load the full-session report"
                  : "Select a class to load the attendance report"}
              </p>
            </div>

            <div className="trep-preview__frame-wrap">
              {canPreview && (loadingSamples || iframeLoading) ? (
                <div className="trep-preview__loader">
                  <MyLoader />
                </div>
              ) : null}

              {!canPreview ? (
                <div className="trep-preview__empty">
                  <div className="sar-empty__icon" aria-hidden>
                    {reportMode === REPORT_MODE_SINGLE ? (
                      <FaUserGraduate />
                    ) : (
                      <FaUsers />
                    )}
                  </div>
                  <p>
                    {reportMode === REPORT_MODE_SINGLE
                      ? "Select a student to preview the full-session attendance report."
                      : "Select a class to preview the attendance report."}
                  </p>
                </div>
              ) : fileUrl ? (
                <Iframe
                  key={`${fileUrl}#${refreshNonce}`}
                  src={fileUrl}
                  width="100%"
                  height={iframeHeight}
                  id="studentAttendanceReportFrame"
                  className="trep-preview__iframe"
                  style={{ border: "none", overflow: "hidden" }}
                  scrolling="no"
                  onLoad={() => setIframeLoading(false)}
                />
              ) : (
                <div className="trep-preview__empty">
                  Waiting for report template…
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {studentPickerOpen ? (
        <MdlSelectSingleStudent
          action={() => setStudentPickerOpen(false)}
          action2={handleSelectedStudent}
        />
      ) : null}
    </div>
  );
}
